import React, { useEffect, useState } from "react";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useQueryString } from "@/hooks";
import { usePartnerQuery } from "../../api";
import { useAuthV2 } from "@/offering/auth_portal_custom_login_provider";
import { colors } from "@/offering_v2/components/colors";
import { ForgotPasswordForm } from "../components/forgot_password_form";
import { isEmail } from "@/lib/validation/string";
import { PartnerConfig } from "../../partner_config";
import { portalAuthPaths } from "../../portal_paths";
import {
  storePartnerSlug,
  usePortalPartnerSlug,
} from "../../use_portal_partner_slug";

export function PortalLoginPage(props: { partnerConfig: PartnerConfig }) {
  const { partnerConfig } = props;
  const query = useQueryString();
  const partnerSlug = usePortalPartnerSlug();
  const auth0 = useAuthV2();
  const partner = usePartnerQuery(partnerSlug);
  const [email, setEmail] = useState(query.email);
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if ((value && isEmail(value)) || !value) {
      setEmailError("");
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !isEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!email || !isEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      await auth0.login(email, password);
    } catch (err) {
      setError("Invalid email or password");
      setIsLoading(false);
    }
  };

  const handleForgotPasswordComplete = (email: string) => {
    setShowForgotPassword(false);
    setSuccessMessage(
      "Password reset instructions have been sent to your email"
    );
  };

  useEffect(() => {
    if (partnerSlug) {
      storePartnerSlug(partnerSlug);
    }
  }, [partnerSlug]);

  if (!partner) {
    return (
      <Container className="mt-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <div className="d-flex flex-column align-items-center">
        {(partnerConfig.logoURL || partner.logoUrl) && (
          <img
            src={partnerConfig.logoURL || partner.logoUrl}
            alt={partner.name}
            style={{ maxWidth: "120px", marginBottom: "0.5rem" }}
          />
        )}
        <div className="col-12 col-md-6 col-lg-4">
          <h2 className="text-center mb-4">Welcome to {partner.name}</h2>
          {error && (
            <Alert className="mb-2" variant="danger">
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert className="mb-2" variant="success">
              {successMessage}
            </Alert>
          )}

          {showForgotPassword ? (
            <ForgotPasswordForm
              onComplete={handleForgotPasswordComplete}
              onGoBack={() => setShowForgotPassword(false)}
              initialEmail={email}
            />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="text"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  required
                  isInvalid={!!emailError}
                />
                {emailError && (
                  <Form.Control.Feedback type="invalid">
                    {emailError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label>Password</Form.Label>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => {
                      setShowForgotPassword(true);
                    }}
                    style={{ color: colors.eggPlantCore }}
                  >
                    Forgot password?
                  </Button>
                </div>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
                className="w-100 d-flex align-items-center justify-content-center gap-2 mt-4"
              >
                {isLoading && <Spinner animation="border" size="sm" />}
                {isLoading ? "Logging in..." : "Login"}
              </Button>

              <div className="text-center mt-3">
                <a
                  style={{ color: colors.eggPlantCore }}
                  href={`${portalAuthPaths.signup}?partnerSlug=${partnerSlug}${
                    email ? `&email=${email}` : ""
                  }&screen=signup`}
                >
                  Don't have an account? Sign up
                </a>
              </div>
            </Form>
          )}
        </div>
      </div>
    </Container>
  );
}
